import React, { useEffect, useRef, useState } from 'react';

const BRAND = {
  cyan: '#00AEEF',
  magenta: '#EC008C',
  red: '#ED1C24',
};

const features = [
  {
    id: 1,
    title: 'Direct MHADA Allocation',
    desc: 'No resale margin. Get genuine government-approved homes.',
    color: BRAND.cyan,
    icon: (
      <>
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
  },
  {
    id: 2,
    title: 'Transparent Breakup',
    desc: 'Complete clarity on every single cost component.',
    color: BRAND.magenta,
    icon: (
      <>
        <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
        <path d="M22 12A10 10 0 0 0 12 2v10z" />
      </>
    ),
  },
  {
    id: 3,
    title: '90% Bank Loan Tie-ups',
    desc: 'Partnered with leading national lenders for easy funding.',
    color: BRAND.red,
    icon: (
      <>
        <line x1="3" x2="21" y1="22" y2="22" />
        <line x1="6" x2="6" y1="18" y2="11" />
        <line x1="10" x2="10" y1="18" y2="11" />
        <line x1="14" x2="14" y1="18" y2="11" />
        <line x1="18" x2="18" y1="18" y2="11" />
        <polygon points="12 2 20 7 4 7" />
      </>
    ),
  },
  {
    id: 4,
    title: 'Full Documentation',
    desc: 'End-to-end registration and legal support under one roof.',
    color: BRAND.cyan,
    icon: (
      <>
        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
        <path d="m9 15 2 2 4-4" />
      </>
    ),
  },
];

const TRUST_STATS = [
  { value: '100%', label: 'Authorized', color: BRAND.cyan },
  { value: '90%', label: 'Loan Support', color: BRAND.magenta },
  { value: '1 BHK', label: 'MHADA Homes', color: BRAND.red },
];

function FeatureIcon({ color, children }) {
  return (
    <div
      className="flex h-12 w-12 items-center justify-center rounded-2xl transition-all duration-300 group-hover:scale-110"
      style={{
        background: `${color}14`,
        boxShadow: `0 0 24px ${color}18`,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </svg>
    </div>
  );
}

function FeatureCard({ feature }) {
  return (
    <div
      className="about-reveal about-card-shine group relative rounded-3xl p-[2px] ses-brand-gradient transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-slate-200/80"
      data-about-reveal
    >
      <div className="relative h-full rounded-[1.35rem] bg-white p-6 overflow-hidden shadow-sm border border-slate-100">
        <div
          className="absolute -top-8 -right-8 w-28 h-28 rounded-full blur-2xl opacity-20 transition-opacity duration-500 group-hover:opacity-40"
          style={{ background: feature.color }}
        />
        <FeatureIcon color={feature.color}>{feature.icon}</FeatureIcon>
        <h3 className="relative mt-5 mb-2 text-lg font-bold text-slate-900 leading-tight">
          {feature.title}
        </h3>
        <p className="relative text-sm text-slate-500 leading-relaxed">{feature.desc}</p>
        <div
          className="absolute bottom-0 left-0 right-0 h-1 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left ses-brand-gradient"
        />
      </div>
    </div>
  );
}

export default function AboutAgency() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    sectionRef.current?.querySelectorAll('[data-about-reveal]').forEach((node, i) => {
      node.style.transitionDelay = `${i * 100}ms`;
      node.classList.add('about-visible');
    });
  }, [visible]);

  return (
    <section
      ref={sectionRef}
      className="relative z-0 overflow-hidden bg-white px-4 md:px-10 pt-28 md:pt-36 pb-20 md:pb-28 font-display"
    >
      {/* Light gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-white pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#00AEEF]/8 via-[#EC008C]/5 to-transparent pointer-events-none" />
      <div className="absolute top-1/3 -left-24 w-72 h-72 rounded-full bg-[#00AEEF]/12 blur-[90px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-24 w-80 h-80 rounded-full bg-[#EC008C]/10 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-48 bg-gradient-to-t from-[#ED1C24]/6 to-transparent blur-2xl pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 ses-brand-gradient opacity-60" />

      <div className="relative mx-auto max-w-7xl">
        <div className="flex flex-col lg:flex-row gap-14 lg:gap-20 items-start">
          {/* Left column */}
          <div className="w-full lg:w-[46%] lg:sticky lg:top-28">
            <div className={`about-reveal ${visible ? 'about-visible' : ''}`}>
              <span className="mb-5 inline-flex items-center gap-2 rounded-full p-[1.5px] ses-brand-gradient shadow-sm">
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={BRAND.cyan}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                  </svg>
                  About The Agency
                </span>
              </span>

              <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-slate-900 leading-[1.15] mb-6">
                Your trusted authorized{' '}
                <span className="ses-brand-text">MHADA partner.</span>
              </h2>

              <div className="space-y-4 text-base md:text-lg text-slate-500 leading-relaxed">
                <p>
                  <strong className="text-slate-900 font-semibold">
                    Sai Enforcement And Security Pvt. Ltd.
                  </strong>{' '}
                  is the authorized agency handling the prestigious MHADA Shirdhon and
                  Khoni-Kalyan projects.
                </p>
                <p>
                  We help families secure affordable, government-approved 1 BHK homes with full
                  clarity on cost, stamp duty, registration, legal fees, and home loan
                  assistance—all seamlessly managed under one roof.
                </p>
              </div>

              {/* Trust stats */}
              <div className="flex flex-wrap gap-3 mt-8">
                {TRUST_STATS.map((stat) => (
                  <div
                    key={stat.label}
                    className="flex flex-col px-4 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm"
                  >
                    <span className="text-xl font-black" style={{ color: stat.color }}>
                      {stat.value}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mt-0.5">
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <button
                  onClick={() =>
                    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="relative overflow-hidden group inline-flex items-center gap-2.5 rounded-2xl px-7 py-4 text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,174,239,0.3)]"
                >
                  <span className="absolute inset-0 ses-brand-gradient opacity-90 group-hover:opacity-100 transition-opacity" />
                  <span className="relative">Talk to our Experts</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="relative transition-transform group-hover:translate-x-1"
                  >
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Right column — bento feature grid */}
          <div className="w-full lg:w-[54%]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {features.map((feature) => (
                <FeatureCard key={feature.id} feature={feature} />
              ))}
            </div>

            {/* Floating trust banner */}
            <div
              className={`about-reveal mt-5 rounded-3xl p-[2px] ses-brand-gradient about-float-slow shadow-lg ${
                visible ? 'about-visible' : ''
              }`}
              data-about-reveal
              style={{ transitionDelay: '450ms' }}
            >
              <div className="rounded-[1.35rem] bg-white px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    {[BRAND.cyan, BRAND.magenta, BRAND.red].map((c) => (
                      <div
                        key={c}
                        className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center shadow-sm"
                        style={{ background: `${c}22` }}
                      >
                        <div className="w-2 h-2 rounded-full" style={{ background: c }} />
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Government Approved</p>
                    <p className="text-xs text-slate-500">Shirdhon & Khoni-Kalyan Projects</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00AEEF] opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00AEEF]" />
                  </span>
                  Verified Agency
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
